import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { AUTH_REDIRECT_EVENT } from '@/api/client'

/**
 * Route gate. Children render only when /api/me returns a user. While the
 * probe is in flight we render nothing (avoids a flash of authed UI for
 * anonymous users). On absence: redirect to /login, preserving where the user
 * was trying to go via the `from` location state.
 *
 * Also installs the global 401-listener that converts axios interceptor events
 * into router navigations — mounted here so it ties to a routed component's
 * lifecycle and doesn't leak when the app unmounts.
 */
export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading } = useCurrentUser()
  const location = useLocation()

  useEffect(() => {
    const onAuthRequired = () => {
      // The router doesn't have a programmatic navigation handle here — but a
      // hash mutation triggers HashRouter to remount, which lands on /login.
      window.location.hash = `#/login`
    }
    window.addEventListener(AUTH_REDIRECT_EVENT, onAuthRequired)
    return () => window.removeEventListener(AUTH_REDIRECT_EVENT, onAuthRequired)
  }, [])

  if (isLoading) return null
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />
  return <>{children}</>
}
